import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../contexts/NotificationContext';
import Loading from '../common/Loading';
import Button from '../common/Button';

const Dashboard = () => {
  const { userProfile } = useAuth();
  const { notify } = useNotification();
  const [stats, setStats] = useState({
    totalProjects: 0,
    openIssues: 0,
    inProgress: 0,
    completed: 0
  });
  const [recentIssues, setRecentIssues] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!userProfile) return;

      try {
        // Get user's projects (all users can see projects they're members of)
        const projectsQuery = query(
          collection(db, 'projects'),
          where('members', 'array-contains', userProfile.uid)
        );
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectsData = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const projectIds = projectsData.map(project => project.id);
        
        // Set recent projects
        setRecentProjects(projectsData.slice(0, 5));

        // Get issues from user's projects
        let issuesData = [];
        if (projectIds.length > 0) {
          const issuesQuery = query(
            collection(db, 'issues'),
            where('projectId', 'in', projectIds),
            orderBy('createdAt', 'desc')
          );
          const issuesSnapshot = await getDocs(issuesQuery);
          issuesData = issuesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        }

        // Calculate stats
        const openIssues = issuesData.filter(issue => 
          issue.status !== 'done' && issue.status !== 'completed'
        ).length;
        
        const inProgress = issuesData.filter(issue => 
          issue.status === 'in-progress'
        ).length;
        
        const completed = issuesData.filter(issue => 
          issue.status === 'done' || issue.status === 'completed'
        ).length;

        setStats({
          totalProjects: projectsData.length,
          openIssues,
          inProgress,
          completed
        });

        // Set recent issues
        setRecentIssues(issuesData.slice(0, 5));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        notify.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userProfile, notify]);

  if (loading) {
    return <Loading text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <div className="flex items-center space-x-4">
          <span className="text-gray-500 dark:text-gray-400">
            Welcome back, {userProfile?.displayName}!
          </span>
          {userProfile?.role === 'admin' && (
            <span className="px-3 py-1 text-sm bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 rounded-full">
              Admin
            </span>
          )}
          {userProfile?.role === 'manager' && (
            <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full">
              Manager
            </span>
          )}
        </div>
      </div>

      {/* Stats Grid - Shows for ALL users */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <span className="text-2xl">📁</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Total Projects
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.totalProjects}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <span className="text-2xl">📋</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Open Issues
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.openIssues}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <span className="text-2xl">🔄</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                In Progress
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.inProgress}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Completed
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats.completed}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects - Shows for ALL users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Projects
            </h2>
            <Button
              variant="outline"
              size="small"
              onClick={() => window.location.href = '/projects'}
            >
              View All
            </Button>
          </div>
          
          {recentProjects.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 dark:text-gray-500 text-4xl mb-2">📁</div>
              <p className="text-gray-500 dark:text-gray-400">No projects found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                {userProfile?.role === 'admin' || userProfile?.role === 'manager'
                  ? 'Create a project to get started' 
                  : 'You are not a member of any projects yet'
                }
              </p>
              {(userProfile?.role === 'admin' || userProfile?.role === 'manager') && (
                <Button
                  onClick={() => window.location.href = '/projects?create=true'}
                  variant="primary"
                  className="mt-4"
                >
                  Create Project
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {recentProjects.map(project => (
                <div key={project.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{project.emoji}</span>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {project.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {project.key} • {project.members?.length || 0} members
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => window.location.href = `/projects/${project.id}`}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Issues - Shows for ALL users */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Issues
            </h2>
            <Button
              variant="outline"
              size="small"
              onClick={() => window.location.href = '/issues'}
            >
              View All
            </Button>
          </div>
          
          {recentIssues.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 dark:text-gray-500 text-4xl mb-2">📋</div>
              <p className="text-gray-500 dark:text-gray-400">No issues found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                {stats.totalProjects === 0 
                  ? 'Join a project to see issues' 
                  : 'No issues created in your projects yet'
                }
              </p>
              {stats.totalProjects > 0 && (
                <Button
                  onClick={() => window.location.href = '/issues?create=true'}
                  variant="primary"
                  className="mt-4"
                >
                  Create Issue
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {recentIssues.map(issue => (
                <div key={issue.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-white truncate">
                      {issue.issueKey}: {issue.title}
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        issue.status === 'todo' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                        issue.status === 'in-progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                        issue.status === 'in-review' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                      }`}>
                        {issue.status.replace('-', ' ')}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                        {issue.type}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => window.location.href = `/issues/${issue.id}`}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions - Only for Admin and Manager */}
      {(userProfile?.role === 'admin' || userProfile?.role === 'manager') && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button
              onClick={() => window.location.href = '/projects?create=true'}
              variant="primary"
              className="w-full"
            >
              Create Project
            </Button>
            {userProfile?.role === 'admin' && (
              <Button
                onClick={() => window.location.href = '/users'}
                variant="outline"
                className="w-full"
              >
                Manage Users
              </Button>
            )}
            <Button
              onClick={() => window.location.href = '/reports'}
              variant="outline"
              className="w-full"
            >
              View Reports
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;